import React, { useState, useEffect, useCallback } from 'react';
import Icon from 'components/AppIcon';
import { auditLogService } from '../../../services/metalguard';
import { SkeletonRow } from '../../../components/skeletons';

const ACTION_COLORS = {
  CREATE: 'bg-emerald-100 text-emerald-700',
  UPDATE: 'bg-blue-100 text-blue-700',
  DELETE: 'bg-red-100 text-red-700',
};

const ENTITY_LABELS = {
  metals: 'Metal',
  scrap_categories: 'Categoría',
  batches: 'Lote',
  sales: 'Venta',
  volatility_settings: 'Volatilidad',
  risk_thresholds: 'Umbrales',
  market_ticks: 'Tick de Mercado',
};

function diffSummary(oldVals, newVals) {
  if (!oldVals && !newVals) return '—';
  if (!oldVals) return 'Creado';
  if (!newVals) return 'Eliminado';
  const changed = [];
  const allKeys = new Set([...Object.keys(oldVals || {}), ...Object.keys(newVals || {})]);
  const skip = new Set(['user_id', 'created_at', 'updated_at', 'id']);
  for (const key of allKeys) {
    if (skip?.has(key)) continue;
    const ov = oldVals?.[key];
    const nv = newVals?.[key];
    if (JSON.stringify(ov) !== JSON.stringify(nv)) {
      changed?.push(key);
    }
  }
  if (changed?.length === 0) return 'Sin cambios';
  return changed?.slice(0, 3)?.join(', ') + (changed?.length > 3 ? ` +${changed?.length - 3}` : '');
}

const PAGE_SIZE = 50;

export default function AuditLogTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [filterAction, setFilterAction] = useState('');
  const [filterEntity, setFilterEntity] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, count } = await auditLogService?.getAll({
      page,
      pageSize: PAGE_SIZE,
      action: filterAction || undefined,
      entityType: filterEntity || undefined,
      dateFrom: filterDateFrom || undefined,
      dateTo: filterDateTo || undefined,
    });
    setLogs(data);
    setTotal(count);
    setLoading(false);
  }, [page, filterAction, filterEntity, filterDateFrom, filterDateTo]);

  useEffect(() => { load(); }, [load]);

  const handleFilterChange = () => { setPage(0); load(); };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-5">
      {/* Filters */}
      <div className="bg-card border border-border rounded-xl p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Acción</label>
            <select
              value={filterAction}
              onChange={e => { setFilterAction(e?.target?.value); setPage(0); }}
              className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
              style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
            >
              <option value="">Todas</option>
              <option value="CREATE">CREATE</option>
              <option value="UPDATE">UPDATE</option>
              <option value="DELETE">DELETE</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Entidad</label>
            <select
              value={filterEntity}
              onChange={e => { setFilterEntity(e?.target?.value); setPage(0); }}
              className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
              style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
            >
              <option value="">Todas</option>
              {Object.entries(ENTITY_LABELS)?.map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Desde</label>
            <input
              type="date"
              value={filterDateFrom}
              onChange={e => { setFilterDateFrom(e?.target?.value); setPage(0); }}
              className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
              style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Hasta</label>
            <input
              type="date"
              value={filterDateTo}
              onChange={e => { setFilterDateTo(e?.target?.value); setPage(0); }}
              className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
              style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
            />
          </div>
          <button
            onClick={() => { setFilterAction(''); setFilterEntity(''); setFilterDateFrom(''); setFilterDateTo(''); setPage(0); }}
            className="flex items-center gap-1.5 px-3 py-2 text-sm border border-border rounded-lg bg-muted text-foreground hover:bg-border transition-colors"
            style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
          >
            <Icon name="RotateCcw" size={13} color="currentColor" />
            Limpiar
          </button>
          <button
            onClick={load}
            className="flex items-center gap-1.5 px-3 py-2 text-sm border border-border rounded-lg bg-muted text-foreground hover:bg-border transition-colors"
            style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
          >
            <Icon name="RefreshCw" size={13} color="currentColor" />
            Actualizar
          </button>
          <span className="ml-auto text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
            {total} registro{total !== 1 ? 's' : ''}
          </span>
        </div>
      </div>
      {/* Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead className="bg-muted border-b border-border">
                <tr>
                  {['Fecha/Hora', 'Usuario', 'Acción', 'Entidad', 'ID', 'Cambios', '']?.map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap"
                      style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {Array.from({ length: 10 })?.map((_, i) => (
                  <SkeletonRow key={i} cols={7} />
                ))}
              </tbody>
            </table>
          </div>
        ) : logs?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground">
            <Icon name="ClipboardList" size={40} color="currentColor" strokeWidth={1.5} />
            <p className="text-sm" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>No hay registros de auditoría</p>
            <p className="text-xs" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Las acciones de creación, edición y eliminación aparecerán aquí</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead className="bg-muted border-b border-border">
                <tr>
                  {['Fecha/Hora', 'Usuario', 'Acción', 'Entidad', 'ID', 'Cambios', '']?.map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap"
                      style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {logs?.map(log => (
                  <React.Fragment key={log?.id}>
                    <tr className="hover:bg-muted/40 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-xs font-mono text-foreground">
                          {new Date(log.created_at)?.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                          {log?.user_email || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${ACTION_COLORS?.[log?.action] || 'bg-muted text-foreground'}`}
                          style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                          {log?.action}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                          {ENTITY_LABELS?.[log?.entity_type] || log?.entity_type}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-mono text-muted-foreground">
                          {log?.entity_id ? log?.entity_id?.slice(0, 8) + '...' : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                          {diffSummary(log?.old_values, log?.new_values)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {(log?.old_values || log?.new_values) && (
                          <button
                            onClick={() => setExpandedId(expandedId === log?.id ? null : log?.id)}
                            className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                          >
                            <Icon name={expandedId === log?.id ? 'ChevronUp' : 'ChevronDown'} size={14} color="currentColor" />
                          </button>
                        )}
                      </td>
                    </tr>
                    {expandedId === log?.id && (
                      <tr className="bg-muted/30">
                        <td colSpan={7} className="px-4 py-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {log?.old_values && (
                              <div>
                                <p className="text-xs font-semibold text-muted-foreground mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Valores anteriores</p>
                                <pre className="text-xs bg-red-50 border border-red-200 rounded p-2 overflow-auto max-h-32 text-red-800" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                                  {JSON.stringify(log?.old_values, null, 2)}
                                </pre>
                              </div>
                            )}
                            {log?.new_values && (
                              <div>
                                <p className="text-xs font-semibold text-muted-foreground mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Valores nuevos</p>
                                <pre className="text-xs bg-emerald-50 border border-emerald-200 rounded p-2 overflow-auto max-h-32 text-emerald-800" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                                  {JSON.stringify(log?.new_values, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
            Página {page + 1} de {totalPages} · {total} registros
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="flex items-center gap-1 px-3 py-1.5 text-xs border border-border rounded-lg bg-muted text-foreground disabled:opacity-40 hover:bg-border transition-colors"
              style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
            >
              <Icon name="ChevronLeft" size={13} color="currentColor" />
              Anterior
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="flex items-center gap-1 px-3 py-1.5 text-xs border border-border rounded-lg bg-muted text-foreground disabled:opacity-40 hover:bg-border transition-colors"
              style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
            >
              Siguiente
              <Icon name="ChevronRight" size={13} color="currentColor" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
